import { useState } from 'react';
import styled from '@emotion/styled';
import type { Chat } from '@trinity/chat';
import { CheckCircle, Edit3 } from 'react-feather';
import { segmentationBuilderApi } from './segmentation-api';

interface SegmentationRefinementProps {
  queryId: number;
  queryIdentifier: string;
  value: Chat.QueryState.SegmentationRefinement;
}

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 20px;
  max-width: 750px;
  margin: 0 auto;
`;

const Card = styled.div`
  background: #ffffff;
  border: 1px solid #e5e5e5;
  border-radius: 10px;
  padding: 18px;
`;

const Title = styled.h3`
  font-size: 16px;
  font-weight: 600;
  color: #323232;
  margin: 0 0 12px 0;
`;

const AlignmentBox = styled.div`
  padding: 16px;
  background: #f0f7ff;
  border: 1px solid #b3d9ff;
  border-radius: 8px;
  font-size: 14px;
  line-height: 1.6;
  color: #1d4ed8;
`;

const EditTextarea = styled.textarea`
  width: 100%;
  min-height: 100px;
  padding: 12px;
  border-radius: 8px;
  border: 1px solid #e5e5e5;
  font-size: 14px;
  color: #323232;
  font-family: inherit;
  resize: vertical;
  outline: none;
  &:focus {
    border-color: #28aae1;
  }
`;

const ButtonRow = styled.div`
  display: flex;
  gap: 12px;
`;

const AcceptButton = styled.button`
  flex: 1;
  padding: 14px;
  border-radius: 12px;
  border: none;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  background: #4cb847;
  color: #ffffff;
  transition: all 0.2s;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  &:hover:not(:disabled) {
    background: #3da138;
    transform: translateY(-1px);
  }
  &:disabled {
    background: #e5e5e5;
    color: #adadad;
    cursor: not-allowed;
  }
`;

const EditButton = styled.button`
  flex: 1;
  padding: 14px;
  border-radius: 12px;
  border: 1px solid #e5e5e5;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  background: #ffffff;
  color: #626262;
  transition: all 0.2s;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  &:hover:not(:disabled) {
    background: #fafafa;
    border-color: #28aae1;
    color: #28aae1;
  }
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

export function SegmentationRefinement({
  queryId,
  queryIdentifier,
  value,
}: SegmentationRefinementProps) {
  const [editing, setEditing] = useState(false);
  const [editedText, setEditedText] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const handleAccept = async (): Promise<void> => {
    setSubmitting(true);
    try {
      await segmentationBuilderApi.submitRefinement(queryIdentifier, 'accept');
    } finally {
      setSubmitting(false);
    }
  };

  const handleEditSubmit = async (): Promise<void> => {
    setSubmitting(true);
    try {
      await segmentationBuilderApi.submitRefinement(queryIdentifier, 'edit', editedText);
      setEditing(false);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Wrapper>
      <Card>
        <Title>Qualitative Alignment Review</Title>
        <AlignmentBox>
          {value.qualAlignment
            ? JSON.stringify(value.qualAlignment, null, 2)
            : 'Based on your qualitative research, we recommend refining the objective to better align with identified themes and patient segments.'}
        </AlignmentBox>
      </Card>

      {editing ? (
        <Card>
          <Title>Edit Objective</Title>
          <EditTextarea
            value={editedText}
            onChange={(e) => setEditedText(e.target.value)}
            placeholder="Enter your refined objective..."
          />
          <ButtonRow style={{ marginTop: 12 }}>
            <EditButton onClick={() => setEditing(false)} disabled={submitting}>
              Cancel
            </EditButton>
            <AcceptButton onClick={handleEditSubmit} disabled={!editedText.trim() || submitting}>
              {submitting ? 'Reprocessing...' : 'Submit Refinement'}
            </AcceptButton>
          </ButtonRow>
        </Card>
      ) : (
        <ButtonRow>
          <EditButton onClick={() => setEditing(true)} disabled={submitting}>
            <Edit3 size={16} />
            Edit Objective
          </EditButton>
          <AcceptButton onClick={handleAccept} disabled={submitting}>
            <CheckCircle size={16} />
            {submitting ? 'Accepting...' : 'Accept & Continue'}
          </AcceptButton>
        </ButtonRow>
      )}
    </Wrapper>
  );
}
